// from https://github.com/tines/tines/blob/main/frontend/src/components/Workbench/ExistingConversation/useChatScroll.ts

import { useCallback, useEffect, useRef, useState } from "react";

// a few px of slack: mobile browsers report fractional scrollTop
const scrolledToBottom = (c: HTMLElement | null) =>
  c && Math.abs(c.scrollHeight - c.scrollTop - c.clientHeight) < 4;

const scrollToBottom = (c: HTMLElement | null) => {
  if (!c) return;
  c.scrollTo({ top: c.scrollHeight });
};

// for https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
const allChanges = {
  characterData: true,
  attributes: true,
  childList: true,
  subtree: true,
};

export default (
  externalRef?: React.RefObject<HTMLDivElement | null>
): [React.RefObject<HTMLDivElement | null>, () => void, boolean] => {
  const internalRef = useRef<HTMLDivElement | null>(null);
  const scrollContainer = externalRef ?? internalRef;
  const [userControlsScroll, setUserControlsScroll] = useState(false);
  const userControlsRef = useRef(false);
  // Suspends autoscroll while a touch is in progress without showing the
  // scroll-to-bottom button: a tap on a fully-visible chat must not flash it.
  const touchActiveRef = useRef(false);

  const resetScroll = useCallback(() => {
    requestAnimationFrame(() => {
      userControlsRef.current = false;
      setUserControlsScroll(false);
      scrollToBottom(scrollContainer.current);
    });
  }, [scrollContainer]);

  useEffect(() => {
    const container = scrollContainer.current;
    if (!container) return;

    const setUserControls = (value: boolean) => {
      if (userControlsRef.current !== value) {
        userControlsRef.current = value;
        setUserControlsScroll(value);
      }
    };

    const onUserScroll = () => setUserControls(!scrolledToBottom(container));

    // Autoscroll is suspended for the whole touch (via touchActiveRef in the
    // observers): a fling from the bottom still reads at-bottom when the
    // finger lifts, and any programmatic scrollTo cancels iOS momentum.
    const onTouchStart = () => {
      touchActiveRef.current = true;
    };
    const onTouchEnd = () => {
      touchActiveRef.current = false;
      // Keep momentum covered after the finger lifts: if the fling already
      // moved away from the bottom, hand control to the user before the
      // observers resume.
      onUserScroll();
    };

    // only ever CLEARS the state: iOS Safari has no `scrollend`, and setting
    // must stay on user-intent events so autoscroll can't trip it mid-flight
    const onScroll = () => {
      if (userControlsRef.current && scrolledToBottom(container)) {
        setUserControls(false);
      }
    };

    container.addEventListener("wheel", onUserScroll, { passive: true });
    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onUserScroll, { passive: true });
    container.addEventListener("touchend", onTouchEnd, { passive: true });
    container.addEventListener("touchcancel", onTouchEnd, { passive: true });
    container.addEventListener("scrollend", onUserScroll, { passive: true });
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("wheel", onUserScroll);
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onUserScroll);
      container.removeEventListener("touchend", onTouchEnd);
      container.removeEventListener("touchcancel", onTouchEnd);
      container.removeEventListener("scrollend", onUserScroll);
      container.removeEventListener("scroll", onScroll);
    };
  }, [scrollContainer]);

  useEffect(() => {
    const container = scrollContainer.current;
    if (!container || userControlsScroll) return;
    const observer = new MutationObserver(() => {
      if (!touchActiveRef.current) scrollToBottom(container);
    });
    observer.observe(container, allChanges);
    return () => observer.disconnect();
  }, [userControlsScroll, scrollContainer]);

  useEffect(() => {
    const container = scrollContainer.current;
    if (!container || userControlsScroll) return;
    const observer = new ResizeObserver(() => {
      if (!touchActiveRef.current) scrollToBottom(container);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [userControlsScroll, scrollContainer]);

  return [scrollContainer, resetScroll, userControlsScroll];
};
